import { Fragment, useEffect, useState } from 'react';
import {
  DEFAULT_VAT_RATE,
  formatRial,
  INVOICE_STATUS_LABELS_FA,
  type InvoiceStatus,
  type TaxInvoiceDto,
} from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { isInspectable, rejectionReasons } from '../lib/taxInvoiceReasons';
import {
  Button,
  Card,
  Chip,
  ErrorRetry,
  Field,
  Input,
  NumberInput,
  useConfirm,
  useToast,
} from '../ui';

/**
 * فاکتور مالیاتی (صورتحساب الکترونیکی / مودیان) — draft, submit, and track tax invoices. Submitting posts
 * revenue to the ledger automatically (Dr AR / Cr Sales / Cr VAT). Uses the configured gateway (a test
 * gateway until Kia's معتمد/certificate is set). Finance/Admin only (mounted inside the ledger screen).
 */

interface InvoiceRow {
  id: number;
  no: number;
  status: InvoiceStatus;
  issuedAt: string;
  buyerName: string;
  total: number;
  taxid: string | null;
}
interface LineDraft {
  productCode: string;
  description: string;
  qty: number;
  unitPrice: number;
  discount: number;
  vatRate: number;
}
interface DealOption {
  id: number;
  no: string;
  customer_name: string | null;
  state: string;
}
interface InvoicePrefill {
  dealId: number;
  customerId: number | null;
  buyerName: string;
  buyerEconomicCode: string;
  buyerNationalId: string;
  source: 'deliveries' | 'items';
  lines: LineDraft[];
}

const STATUS_TONE: Record<InvoiceStatus, 'neutral' | 'info' | 'success' | 'danger' | 'warn'> = {
  draft: 'neutral',
  submitted: 'info',
  accepted: 'success',
  rejected: 'danger',
  cancelled: 'warn',
};

const emptyLine = (): LineDraft => ({
  productCode: '',
  description: '',
  qty: 1,
  unitPrice: 0,
  discount: 0,
  vatRate: DEFAULT_VAT_RATE,
});

export function TaxInvoicePanel({
  focusDealId = null,
  onFocused,
}: {
  /** deep-link from a delivered deal (E13-10): auto-open the form + prefill from this deal on arrival. */
  focusDealId?: number | null;
  onFocused?: (() => void) | undefined;
} = {}) {
  const toast = useToast();
  const confirm = useConfirm();
  const rowsRes = useApiResource<{ invoices: InvoiceRow[] }>('/api/accounting/invoices');
  const rows = rowsRes.data?.invoices ?? [];
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [issuedAt, setIssuedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [buyerName, setBuyerName] = useState('');
  const [buyerEcon, setBuyerEcon] = useState('');
  const [buyerNat, setBuyerNat] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  // Linkage to the source sale — sent on create so the invoice ties back to the deal/customer (E4-02).
  const [dealId, setDealId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [deals, setDeals] = useState<DealOption[]>([]);
  // Detail drill-in (OPS-08): the list projection drops errors_json, so a rejected row's INTA reasons live
  // only behind the already-gated GET :id. Opening a non-draft row fetches its full DTO and shows the outcome
  // (rejection reasons / taxid) that the row alone can't carry.
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TaxInvoiceDto | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);

  const refresh = () => rowsRes.reload();

  async function toggleDetail(id: number) {
    if (detailId === id) {
      setDetailId(null);
      setDetail(null);
      return;
    }
    setDetailId(id);
    setDetail(null);
    setDetailBusy(true);
    const r = await api<TaxInvoiceDto>(`/api/accounting/invoices/${id}`);
    setDetailBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در بارگذاری فاکتور'), 'error');
      setDetailId(null);
      return;
    }
    setDetail(r.body as TaxInvoiceDto);
  }
  useEffect(() => {
    // secondary: the deal dropdown (an empty list on failure is a minor degradation, not a blank screen)
    void api<DealOption[]>('/api/deals').then((r) => {
      if (r.ok) setDeals(r.body as DealOption[]);
    });
  }, []);

  function resetForm() {
    setBuyerName('');
    setBuyerEcon('');
    setBuyerNat('');
    setDealId(null);
    setCustomerId(null);
    setLines([emptyLine()]);
  }

  // Prefill buyer identity + lines FROM a delivered deal so a tax invoice is issued from the sale record,
  // not re-keyed. The operator still reviews every field (and maps productCode → sstid) before submitting.
  async function prefillFromDeal(id: number) {
    if (!id) return;
    const r = await api<InvoicePrefill>(`/api/deals/${id}/invoice-prefill`);
    if (!r.ok) {
      toast('خطا در بارگذاری قرارداد', 'error');
      return;
    }
    const p = r.body as InvoicePrefill;
    setDealId(p.dealId);
    setCustomerId(p.customerId);
    setBuyerName(p.buyerName);
    setBuyerEcon(p.buyerEconomicCode);
    setBuyerNat(p.buyerNationalId);
    setLines(p.lines.length ? p.lines : [emptyLine()]);
    toast(p.source === 'deliveries' ? 'از تحویل‌های قرارداد پر شد ✓' : 'از اقلام قرارداد پر شد ✓');
  }

  // Deep-link from a deal (E13-10): «صدور فاکتور مالیاتی» on the deal lands here with the form open and
  // prefilled, instead of the finance user re-picking the deal from the dropdown.
  useEffect(() => {
    if (focusDealId == null) return;
    setOpen(true);
    void prefillFromDeal(focusDealId);
    onFocused?.();
  }, [focusDealId]);

  // Live preview of totals (mirrors the server's computeInvoice math).
  const preview = lines.reduce(
    (t, l) => {
      const gross = Math.round(l.qty * l.unitPrice);
      const net = Math.max(0, gross - l.discount);
      const vat = Math.round(net * l.vatRate);
      return { net: t.net + net, vat: t.vat + vat, total: t.total + net + vat };
    },
    { net: 0, vat: 0, total: 0 },
  );

  function setLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function createDraft() {
    if (!buyerName.trim() || lines.every((l) => !l.productCode.trim())) {
      toast('نام خریدار و حداقل یک ردیف لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api<{ id: number }>('/api/accounting/invoices', {
      method: 'POST',
      body: JSON.stringify({
        ...(dealId ? { dealId } : {}),
        ...(customerId ? { customerId } : {}),
        issuedAt,
        buyerName,
        buyerEconomicCode: buyerEcon,
        buyerNationalId: buyerNat,
        lines: lines
          .filter((l) => l.productCode.trim())
          .map((l) => ({
            productCode: l.productCode,
            description: l.description,
            qty: l.qty,
            unitPrice: l.unitPrice,
            discount: l.discount,
            vatRate: l.vatRate,
          })),
      }),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ایجاد فاکتور', 'error');
      return;
    }
    toast('پیش‌نویس فاکتور ساخته شد ✓');
    resetForm();
    refresh();
  }

  async function submit(id: number) {
    if (
      !(await confirm({
        title: 'ارسال فاکتور به سامانهٔ مودیان',
        message:
          'فاکتور به‌صورت رسمی به سامانهٔ مؤدیان ارسال و درآمد در دفترکل ثبت می‌شود. فاکتور ارسال‌شده غیرقابل ویرایش است (اصلاح فقط با فاکتور اصلاحی/ابطالی). ارسال شود؟',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    const r = await api<{ status: InvoiceStatus; errors?: string[] }>(
      `/api/accounting/invoices/${id}/submit`,
      { method: 'POST' },
    );
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ارسال', 'error');
      return;
    }
    const b = r.body as { status: InvoiceStatus; errors?: string[] };
    if (b.status === 'accepted') toast('فاکتور تأیید و ثبت شد ✓');
    else toast(`رد شد: ${b.errors?.join('؛ ') ?? 'نامشخص'}`, 'error');
    refresh();
  }

  return (
    <Card title="فاکتور مالیاتی (مودیان)">
      <Button variant="secondary" onClick={() => setOpen((s) => !s)}>
        {open ? 'بستن فرم' : 'فاکتور جدید'}
      </Button>

      {open ? (
        <div style={{ marginTop: 12 }}>
          <Field label="پرکردن از قرارداد (اختیاری)">
            <select
              className="input"
              value={dealId ?? ''}
              onChange={(e) => void prefillFromDeal(Number(e.target.value))}
            >
              <option value="">— انتخاب قرارداد —</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.no} — {d.customer_name ?? 'بدون مشتری'}
                </option>
              ))}
            </select>
          </Field>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
              gap: 12,
              marginTop: 12,
            }}
          >
            <Field label="تاریخ صدور">
              <Input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            </Field>
            <Field label="نام خریدار">
              <Input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            </Field>
            <Field label="شمارهٔ اقتصادی خریدار">
              <Input value={buyerEcon} onChange={(e) => setBuyerEcon(e.target.value)} />
            </Field>
            <Field label="کد/شناسهٔ ملی خریدار">
              <Input value={buyerNat} onChange={(e) => setBuyerNat(e.target.value)} />
            </Field>
          </div>

          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>کد کالا</th>
                <th>شرح</th>
                <th>مقدار</th>
                <th>بهای واحد</th>
                <th>تخفیف</th>
                <th>نرخ مالیات</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <Input
                      value={l.productCode}
                      onChange={(e) => setLine(i, { productCode: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      value={l.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={l.qty}
                      onValue={(v) => setLine(i, { qty: v })}
                      allowDecimal
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={l.unitPrice}
                      onValue={(v) => setLine(i, { unitPrice: v })}
                    />
                  </td>
                  <td>
                    <NumberInput value={l.discount} onValue={(v) => setLine(i, { discount: v })} />
                  </td>
                  <td>
                    <NumberInput
                      value={l.vatRate}
                      onValue={(v) => setLine(i, { vatRate: v })}
                      allowDecimal
                    />
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))
                      }
                      disabled={lines.length <= 1}
                    >
                      حذف
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>
                  <Button
                    variant="secondary"
                    onClick={() => setLines((ls) => [...ls, emptyLine()])}
                  >
                    + ردیف
                  </Button>
                </td>
                <td colSpan={5} style={{ color: 'var(--ink-2)' }}>
                  خالص {formatRial(preview.net)} · مالیات {formatRial(preview.vat)} ·{' '}
                  <b>جمع {formatRial(preview.total)}</b>
                </td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: 10 }}>
            <Button onClick={() => void createDraft()} disabled={busy}>
              {busy ? 'در حال ثبت…' : 'ساخت پیش‌نویس'}
            </Button>
          </div>
        </div>
      ) : null}

      {rowsRes.status === 'error' ? <ErrorRetry error={rowsRes.error} onRetry={refresh} /> : null}

      {rows.length > 0 ? (
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>شماره</th>
              <th>تاریخ</th>
              <th>خریدار</th>
              <th>مبلغ</th>
              <th>وضعیت</th>
              <th>شمارهٔ مالیاتی</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.no}</td>
                  <td>{r.issuedAt}</td>
                  <td>{r.buyerName || '—'}</td>
                  <td>{formatRial(r.total)}</td>
                  <td>
                    <Chip tone={STATUS_TONE[r.status]}>{INVOICE_STATUS_LABELS_FA[r.status]}</Chip>
                  </td>
                  <td
                    style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, direction: 'ltr' }}
                  >
                    {r.taxid ?? '—'}
                  </td>
                  <td>
                    {r.status === 'draft' ? (
                      <Button variant="secondary" onClick={() => void submit(r.id)} disabled={busy}>
                        ارسال
                      </Button>
                    ) : isInspectable(r.status) ? (
                      <Button variant="secondary" onClick={() => void toggleDetail(r.id)}>
                        {detailId === r.id ? 'بستن' : 'جزئیات'}
                      </Button>
                    ) : null}
                  </td>
                </tr>
                {detailId === r.id ? (
                  <tr>
                    <td colSpan={7} style={{ background: 'var(--panel, #fafafa)' }}>
                      {detailBusy && !detail ? (
                        <span style={{ color: 'var(--ink-2)' }}>در حال بارگذاری…</span>
                      ) : detail?.status === 'rejected' ? (
                        <span style={{ color: 'var(--red)' }}>
                          دلیل رد از سامانهٔ مودیان: {rejectionReasons(detail.errors)}
                        </span>
                      ) : detail ? (
                        <span style={{ color: 'var(--ink-2)' }}>
                          {detail.taxid
                            ? `شمارهٔ مالیاتی: ${detail.taxid}`
                            : 'بدون دلیل رد ثبت‌شده'}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      ) : null}
    </Card>
  );
}
